import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../../state/state.interface';
import { IBaseResponse, IGetManyResponse } from '../../../../utilities/http-utilities.service';
import { IHomeBoard, ISidebarBoardResponse } from '../../../home/home.interface';
import { IItemBase } from '../../items.interface';
import { IEditItem } from './item-escalete.interface';

export interface IItemEscalateState {
  boardsLoading: ELoadStatus;
  boards: IHomeBoard[];
  editItemLoading: ELoadStatus;
}

@Injectable()
export class ItemEscalateStore extends ComponentStore<IItemEscalateState> {
  public readonly boards: Signal<IHomeBoard[]> = this.selectSignal((state: IItemEscalateState) => state.boards || []);

  readonly loadBoards = this.effect((trigger$: Observable<{ boardId: number; escalatedBoardId: number }>) =>
    trigger$.pipe(
      switchMap(({ boardId, escalatedBoardId }) => {
        this.patchState({ boards: [], boardsLoading: ELoadStatus.loading });

        return this.getBoards(boardId, escalatedBoardId).pipe(
          tapResponse(
            (output: IGetManyResponse<IHomeBoard>) =>
              this.patchState({
                boards: output.data,
                boardsLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading boards', error),
          ),
        );
      }),
    ),
  );

  readonly editItemEscalatedBoardId = this.effect((trigger$: Observable<IEditItem>) =>
    trigger$.pipe(
      switchMap((editItem: IEditItem) => {
        this.patchState({ editItemLoading: ELoadStatus.loading });

        return this.editItem(editItem).pipe(
          tapResponse(
            () =>
              this.patchState({
                editItemLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(private readonly http: HttpClient) {
    super({
      boards: [],
      boardsLoading: ELoadStatus.initial,
      editItemLoading: ELoadStatus.initial,
    });
  }

  private getBoards(boardId: number, escalatedBoardId: number): Observable<IGetManyResponse<IHomeBoard>> {
    const params = new HttpParams({
      fromObject: {
        additionalBoardId: escalatedBoardId.toString(),
        fetchDirectRelations: 'true',
        parentId: boardId.toString(),
      },
    });

    return this.http.get<IGetManyResponse<ISidebarBoardResponse>>('boards/sidebar', { params });
  }

  private editItem(item: IEditItem): Observable<IBaseResponse<IItemBase>> {
    return this.http.patch<IBaseResponse<IItemBase>>(`items/${item.id}`, {
      assigneeId: null,
      escalatedBoardId: item.escalatedBoardId,
    });
  }
}
